<div class="flex flex-col flex-auto min-w-0">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between p-6 sm:p-8 gap-4">
        <div class="flex-1">
            <div class="flex items-center">
                <div class="text-2xl font-semibold tracking-tight leading-7">Bookings</div>
                <div class="ml-4 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    Total Amount: {{ totalAmount | currency:'AED' }}
                </div>
            </div>
            <div class="mt-1 text-secondary">View booking with user details</div>
        </div>
    </div>
    
    <!-- Filters Card -->
    <div class="px-8">
        <form [formGroup]="reportForm" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <!-- Email Filter -->
                <mat-form-field class="w-full">
                    <mat-label>Email</mat-label>
                    <mat-select formControlName="email">
                        <mat-option value="">All Emails</mat-option>
                        <mat-option *ngFor="let email of userEmails" [value]="email">
                            {{ email }}
                        </mat-option>
                    </mat-select>
                    <mat-icon matPrefix>email</mat-icon>
                    <mat-progress-spinner 
                        *ngIf="isEmailsLoading" 
                        matSuffix 
                        diameter="20" 
                        mode="indeterminate">
                    </mat-progress-spinner>
                    <mat-hint *ngIf="!isEmailsLoading && userEmails.length === 0">
                        No emails available
                    </mat-hint>
                </mat-form-field>

                <!-- Company Filter -->
                <mat-form-field class="w-full">
                    <mat-label>Company</mat-label>
                    <mat-select formControlName="companyId">
                        <mat-option value="">All Companies</mat-option>
                        <mat-option *ngFor="let company of companies" [value]="company.id">
                            {{ company.name }}
                        </mat-option>
                    </mat-select>
                    <mat-icon matPrefix>business</mat-icon>
                    <mat-progress-spinner 
                        *ngIf="isCompaniesLoading" 
                        matSuffix 
                        diameter="20" 
                        mode="indeterminate">
                    </mat-progress-spinner>
                    <mat-hint *ngIf="!isCompaniesLoading && companies.length === 0">
                        No companies available
                    </mat-hint>
                </mat-form-field>

                <!-- Venue Filter -->
                <mat-form-field class="w-full">
                    <mat-label>Venue</mat-label>
                    <mat-select formControlName="venueId">
                        <mat-option value="">All Venues</mat-option>
                        <mat-option 
                            *ngFor="let venue of venues" 
                            [value]="venue._id || venue.id">
                            {{ venue.name || venue.venueName }}
                        </mat-option>
                    </mat-select>
                    <mat-icon matPrefix>location_on</mat-icon>
                    <mat-progress-spinner 
                        *ngIf="isVenuesLoading" 
                        matSuffix 
                        diameter="20" 
                        mode="indeterminate">
                    </mat-progress-spinner>
                </mat-form-field>

                <!-- Court Filter -->
                <mat-form-field class="w-full">
                    <mat-label>Court</mat-label>
                    <mat-select formControlName="courtId">
                        <mat-option value="">All Courts</mat-option>
                        <mat-option 
                            *ngFor="let court of courts" 
                            [value]="court._id || court.id">
                            {{ court.name || court.courtName || court.title }}
                        </mat-option>
                    </mat-select>
                    <mat-icon matPrefix>sports_tennis</mat-icon>
                    <mat-progress-spinner 
                        *ngIf="isCourtsLoading" 
                        matSuffix 
                        diameter="20" 
                        mode="indeterminate">
                    </mat-progress-spinner>
                    <mat-hint *ngIf="!reportForm.get('venueId')?.value">Select a venue first</mat-hint>
                </mat-form-field>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Start Date Filter -->
                <mat-form-field class="w-full">
                    <mat-label>Start Date</mat-label>
                    <input matInput [matDatepicker]="startPicker" formControlName="startDate">
                    <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
                    <mat-datepicker #startPicker></mat-datepicker>
                </mat-form-field>

                <!-- End Date Filter -->
                <mat-form-field class="w-full">
                    <mat-label>End Date</mat-label>
                    <input matInput [matDatepicker]="endPicker" formControlName="endDate">
                    <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                    <mat-datepicker #endPicker></mat-datepicker>
                </mat-form-field>
            </div>
        </form>
    </div>

    <!-- Results Section -->
    <div class="flex flex-col flex-auto p-6 sm:p-8">
        <!-- Loading State -->
        <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
            <mat-spinner diameter="40"></mat-spinner>
            <div class="mt-4 text-secondary">Loading audit data...</div>
        </div>

            <!-- No Results -->
        <div *ngIf="!isLoading && hasSearched && dataSource.data.length === 0" 
            class="flex flex-col items-center justify-center py-12 text-center">
            <mat-icon class="text-gray-400 mb-4" style="font-size: 96px; height: 96px; width: 96px; line-height: 1; display: block;">search_off</mat-icon>
            <div class="text-xl font-medium text-gray-600 mb-2">No bookings found</div>
            <div class="text-gray-500">Try searching with different filters or clear filters to see all bookings</div>
        </div>
        <!-- Results Table -->
        <div *ngIf="!isLoading && hasSearched && dataSource.data.length > 0" class="flex flex-col flex-auto">
            <!-- Summary Stats -->
            <div class="flex flex-row justify-between gap-4 mb-6">
                <div class="flex flex-row gap-4">
                    <div class="px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                        <div class="text-sm text-green-600">Total Bookings</div>
                        <div class="text-lg font-semibold text-green-800">{{ totalItems }}</div>
                    </div>
                    <div class="px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                        <div class="text-sm text-blue-600">Total Revenue</div>
                        <div class="text-lg font-semibold text-blue-800">{{ totalAmount | currency:'AED' }}</div>
                    </div>
                    <div class="px-4 py-2 bg-purple-50 rounded-lg border border-purple-200">
                        <div class="text-sm text-purple-600">Current Page</div>
                        <div class="text-lg font-semibold text-purple-800">{{ dataSource.data.length }} items</div>
                    </div>
                </div>
                <div>
                    <!-- Action Buttons -->
                    <div class="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            mat-raised-button
                            color="primary"
                            (click)="onSubmit()"
                            [disabled]="isLoading"
                            class="sm:w-auto">
                            <div class="flex items-center gap-2">
                                <mat-spinner diameter="16" *ngIf="isLoading"></mat-spinner>
                                <mat-icon>search</mat-icon>
                                <span>{{ isLoading ? 'Searching...' : 'Search' }}</span>
                            </div>
                        </button>

                        <button
                            type="button"
                            mat-stroked-button
                            color="primary"
                            (click)="downloadReport()"
                            [disabled]="isLoading || !hasSearched"
                            class="sm:w-auto">
                            <div class="flex items-center gap-2">
                                <mat-icon>download</mat-icon>
                                <span>Export CSV</span>
                            </div>
                        </button>

                        <!-- Clear Filter Button -->
                        <button
                            type="button"
                            mat-stroked-button
                            (click)="clearFilter()"
                            [disabled]="isLoading"
                            class="sm:w-auto">
                            <div class="flex items-center gap-2">
                                <mat-icon>clear</mat-icon>
                                <span>Clear All Filters</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Table Container -->
            <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div class="overflow-x-auto">
                    <table mat-table [dataSource]="dataSource" class="w-full">
                        <!-- Booking ID Column -->
                        <ng-container matColumnDef="bookingId">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Booking ID</th>
                            <td mat-cell *matCellDef="let element" class="font-mono text-sm">
                                {{ element.bookingId }}
                            </td>
                        </ng-container>

                        <!-- Venue Column -->
                        <ng-container matColumnDef="venue">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Venue</th>
                            <td mat-cell *matCellDef="let element">
                                {{ element.venue }}
                            </td>
                        </ng-container>

                        <!-- Court Column -->
                        <ng-container matColumnDef="court">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Court</th>
                            <td mat-cell *matCellDef="let element">
                                {{ element.court }}
                            </td>
                        </ng-container>

                        <!-- Date Column -->
                        <ng-container matColumnDef="date">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Date</th>
                            <td mat-cell *matCellDef="let element" class="whitespace-nowrap">
                                {{ element.date }}
                            </td>
                        </ng-container>

                        <!-- Duration Column -->
                        <ng-container matColumnDef="duration">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Duration</th>
                            <td mat-cell *matCellDef="let element" class="text-center">
                                <span class="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                                    {{ element.duration }}
                                </span>
                            </td>
                        </ng-container>

                        <!-- Price Column -->
                        <ng-container matColumnDef="price">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold text-right">Price</th>
                            <td mat-cell *matCellDef="let element" class="text-right font-medium">
                                {{ element.price | currency:'AED' }}
                            </td>
                        </ng-container>

                        <!-- Status Column -->
                        <ng-container matColumnDef="status">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Status</th>
                            <td mat-cell *matCellDef="let element">
                                <div class="flex items-center gap-2">
                                    <span [class]="'px-2 py-1 rounded-full text-xs font-medium ' + getStatusClass(element.status)">
                                        {{ element.status | titlecase }}
                                    </span>
                                    <mat-icon class="text-base" [matTooltip]="element.status | titlecase">
                                        {{ getStatusIcon(element.status) }}
                                    </mat-icon>
                                </div>
                            </td>
                        </ng-container>

                        <!-- Company Column -->
                        <ng-container matColumnDef="company">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Company</th>
                            <td mat-cell *matCellDef="let element">
                                {{ element.company }}
                            </td>
                        </ng-container>

                        <!-- Booking Amount Column -->
                        <ng-container matColumnDef="bookingAmount">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold text-right">Booking Amount</th>
                            <td mat-cell *matCellDef="let element" class="text-right font-medium text-green-600">
                                {{ element.bookingAmount | currency:'AED' }}
                            </td>
                        </ng-container>

                        <!-- Name Column -->
                        <ng-container matColumnDef="name">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Booked By</th>
                            <td mat-cell *matCellDef="let element">
                                <div class="flex flex-col">
                                    <span class="font-medium">{{ element.name }}</span>
                                    <span class="text-xs text-gray-500">{{ element.role | titlecase }}</span>
                                </div>
                            </td>
                        </ng-container>

                        <!-- Email Column -->
                        <ng-container matColumnDef="email">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Email</th>
                            <td mat-cell *matCellDef="let element" class="text-sm">
                                <a [href]="'mailto:' + element.email" class="text-blue-600 hover:text-blue-800 hover:underline">
                                    {{ element.email }}
                                </a>
                            </td>
                        </ng-container>

                        <!-- Role Column -->
                        <ng-container matColumnDef="role">
                            <th mat-header-cell *matHeaderCellDef class="font-semibold">Role</th>
                            <td mat-cell *matCellDef="let element">
                                <span [class]="'px-2 py-1 rounded text-xs font-medium ' + getRoleClass(element.role)">
                                    {{ element.role | titlecase }}
                                </span>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                        <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
                            class="hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100"></tr>
                    </table>
                </div>

                <!-- Pagination -->
                <mat-paginator
                    [length]="totalItems"
                    [pageSize]="pageSize"
                    [pageIndex]="pageIndex"
                    [pageSizeOptions]="[5, 10, 25, 50]"
                    (page)="onPageChange($event)"
                    showFirstLastButtons
                    class="border-t">
                </mat-paginator>
            </div>

            <!-- Results Info -->
            <div class="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600">
                <div>
                    Showing {{ (pageIndex * pageSize) + 1 }} to 
                    {{ getEndIndex() }} of {{ totalItems }} entries
                </div>
                <div class="mt-2 sm:mt-0">
                    Total Revenue: <span class="font-semibold text-green-600">{{ totalAmount | currency:'AED' }}</span>
                </div>
            </div>
        </div>
    </div>
</div>